// PdfViewerModal.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.js`;
const Overlay = styled.div`
  position: fixed;
  top: 220px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
`;
const Modal = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 1000px;
  max-height: 550px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const Header = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;
const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 20px;
  color: #6B7280;
  &:hover {
    color: #111827;
  }
`;
const Body = styled.div`
  flex: 1;
  overflow: auto;
  background: #111827;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;
const CanvasWrapper = styled.div`
  position: relative;
  padding: 16px;
  width: 100%;
  display: flex;
  justify-content: center;
`;
const PdfCanvas = styled.canvas`
  display: block;
`;
const TextLayer = styled.div`
  position: absolute;
  top: 16px; /* same padding */
  left: 50%;
  transform: translateX(-50%);
  pointer-events: auto;
  width: fit-content;
  height: fit-content;
`;
const Controls = styled.div`
  padding: 8px 16px;
  border-top: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;
const Button = styled.button`
  border-radius: 8px;
  border: 1px solid #D1D5DB;
  background: #F9FAFB;
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
  }
  &:hover:not(:disabled) {
    background: #EEF2FF;
    border-color: #C7D2FE;
  }
`;
const PageInfo = styled.div`
  font-size: 13px;
  color: #4B5563;
`;
const HighlightMark = styled.mark`
  background: #FDE68A;
  padding: 0 2px;
  border-radius: 3px;
  box-shadow: 0 0 0 1px #F59E0B;
`;
export default function PdfViewerModal({
  pdfUrl,
  initialPage = 1,
  onClose,
  title,
  highlightText,
}) {
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [textLayerReady, setTextLayerReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const load = async () => {
      try {
        if (!pdfUrl) throw new Error('No PDF URL was provided for this reference.');
        const doc = await pdfjsLib.getDocument(pdfUrl).promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setCurrentPage(
          Math.min(Math.max(initialPage || 1, 1), doc.numPages)
        );
      } catch (err) {
        console.error("Error loading PDF:", err);
        if (!cancelled) setLoadError(err.message || 'Failed to load PDF.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, initialPage]);
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let renderTask;
    const render = async () => {
      try {
        setTextLayerReady(false);
        const page = await pdfDoc.getPage(currentPage);
        const parent = canvasRef.current.parentElement;
        const viewport = page.getViewport({ scale: 1 });
        const desiredWidth = Math.min(parent.clientWidth - 32, 900);
        const scale = desiredWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        if (textLayerRef.current) {
          textLayerRef.current.style.setProperty(
            "--scale-factor",
            scale
          );
        }
        renderTask = page.render({
          canvasContext: ctx,
          viewport: scaledViewport,
        });
        await renderTask.promise;
        const textContent = await page.getTextContent();
        if (textLayerRef.current) {
          textLayerRef.current.innerHTML = "";
          await pdfjsLib.renderTextLayer({
            textContent,
            container: textLayerRef.current,
            viewport: scaledViewport,
            textDivs: [],
            enhanceTextSelection: true,
          }).promise;
          setTextLayerReady(true);
        }
      } catch (error) {
        console.error("Error rendering page:", error);
      }
    };
    render();
    return () => {
      if (renderTask && renderTask.cancel) renderTask.cancel();
    };
  }, [pdfDoc, currentPage]);
  useEffect(() => {
    if (!highlightText || !textLayerReady || !textLayerRef.current) return;

    const container = textLayerRef.current;
    const spans = Array.from(container.querySelectorAll("span"));
    if (!spans.length) return;

    // Reconstruct the page's text as one continuous string (in reading order)
    // instead of matching span-by-span — pdf.js splits text into a span per
    // font/positioning run, which has nothing to do with word or sentence
    // boundaries, so a citation or phrase frequently spans multiple spans.
    // Searching the joined string finds it regardless of how it was split.
    const spanRanges = [];
    let joined = "";
    for (const span of spans) {
      const text = span.textContent || "";
      const start = joined.length;
      joined += text;
      spanRanges.push({ span, start, end: start + text.length });
    }

    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    // Build a regex from a phrase that tolerates whitespace pdf.js may have
    // dropped or added between spans (justified text often omits an actual
    // space character at a word break, relying on glyph positioning instead).
    const buildFlexiblePattern = (phrase, { anchorBoundary = false } = {}) => {
      const words = phrase.trim().split(/\s+/).filter(Boolean);
      if (!words.length) return null;
      const body = words.map(escapeRegExp).join("\\s*");
      const pattern = anchorBoundary ? `(?<![\\d.])${body}(?!\\d)` : body;
      try {
        return new RegExp(pattern, "i");
      } catch {
        return null;
      }
    };

    const target = highlightText.trim();
    const articleMatch = target.match(/^(\d+(\.\d+)*\.)/);
    const tableMatch = target.match(/^(table\s+\d+(\.\d+)*)/i);

    let match = null;

    if (articleMatch) {
      const re = buildFlexiblePattern(articleMatch[1], { anchorBoundary: true });
      if (re) match = re.exec(joined);
    }

    if (!match && tableMatch) {
      const re = buildFlexiblePattern(tableMatch[1]);
      if (re) match = re.exec(joined);
    }

    if (!match) {
      const re = buildFlexiblePattern(target);
      if (re) match = re.exec(joined);
    }

    if (!match) {
      // Fall back to a single distinctive keyword when the full phrase
      // doesn't appear verbatim (e.g. a paraphrased or trimmed snippet).
      const words = target.split(/\s+/).filter(Boolean);
      const keyword = words.find((w) => w.length > 3) || words[0] || "";
      if (keyword) {
        const re = buildFlexiblePattern(keyword);
        if (re) match = re.exec(joined);
      }
    }

    if (!match) {
      console.warn(
        "Highlight text not found on page:",
        highlightText,
        "on page",
        currentPage
      );
      return;
    }

    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;
    const matchedSpans = spanRanges
      .filter((r) => r.start < matchEnd && r.end > matchStart)
      .map((r) => r.span);

    if (!matchedSpans.length) return;

    container
      .querySelectorAll(".pdf-highlight-overlay")
      .forEach((el) => el.remove());

    // A match can wrap across visual lines (a long snippet), so group the
    // matched spans by line and draw one overlay box per line.
    const containerRect = container.getBoundingClientRect();
    const lineThreshold = 1.5;
    const remaining = [...matchedSpans];
    while (remaining.length) {
      const anchorRect = remaining[0].getBoundingClientRect();
      const lineTop = anchorRect.top;
      const lineSpans = remaining.filter((span) => {
        const rect = span.getBoundingClientRect();
        return Math.abs(rect.top - lineTop) < lineThreshold;
      });
      lineSpans.forEach((span) => {
        const idx = remaining.indexOf(span);
        if (idx >= 0) remaining.splice(idx, 1);
      });

      const rects = lineSpans.map((span) => span.getBoundingClientRect());
      const left = Math.min(...rects.map((r) => r.left)) - containerRect.left;
      const right = Math.max(...rects.map((r) => r.right)) - containerRect.left;
      const top = Math.min(...rects.map((r) => r.top)) - containerRect.top;
      const bottom = Math.max(...rects.map((r) => r.bottom)) - containerRect.top;

      const overlay = document.createElement("div");
      overlay.className = "pdf-highlight-overlay";
      overlay.style.position = "absolute";
      overlay.style.left = `${left}px`;
      overlay.style.top = `${top}px`;
      overlay.style.width = `${right - left}px`;
      overlay.style.height = `${bottom - top}px`;
      overlay.style.background = "rgba(253, 230, 138, 0.50)";
      overlay.style.borderRadius = "4px";
      overlay.style.pointerEvents = "none";

      container.appendChild(overlay);
    }

    matchedSpans[0].scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [highlightText, textLayerReady, currentPage]);

  const nextPage = () =>
    setCurrentPage((p) => Math.min(numPages, p + 1));
  const prevPage = () =>
    setCurrentPage((p) => Math.max(1, p - 1));
  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title || "PDF Viewer"}</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        <Body>
          <CanvasWrapper>
            {loading ? (
              <div style={{ color: "#E5E7EB" }}>Loading PDF…</div>
            ) : loadError ? (
              <div style={{ color: "#FCA5A5", maxWidth: 480, textAlign: "center" }}>
                Couldn't load this PDF: {loadError}
              </div>
            ) : (
              <>
                <PdfCanvas ref={canvasRef} />
                <TextLayer
                  ref={textLayerRef}
                  className="textLayer"
                />
              </>
            )}
          </CanvasWrapper>
        </Body>
        <Controls>
          <NavButtons>
            <Button onClick={prevPage} disabled={currentPage <= 1}>
              ‹ Prev
            </Button>
            <Button
              onClick={nextPage}
              disabled={currentPage >= numPages}
            >
              Next ›
            </Button>
          </NavButtons>
          <PageInfo>
            {loading
              ? "Loading…"
              : loadError
                ? "—"
                : `Page ${currentPage} of ${numPages}`}
          </PageInfo>
        </Controls>
      </Modal>
    </Overlay>
  );
}





















